import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import SectionHeader from './SectionHeader';
import PhoneNumberInput from './PhoneNumberInput';
import { formatPhoneForDisplay } from '../utils/phoneNumber';
import './TransactionTeam.css';
import { getBackendBaseUrl } from '../utils/apiUrls';
import useViewerSubscription from '../hooks/useViewerSubscription';

const BASE_URL = getBackendBaseUrl();

const ROLES = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'co_buyer', label: 'Co-Buyer' },
  { value: 'buyers_agent', label: "Buyer's Agent" },
  { value: 'co_buyers_agent', label: "Co-Buyer's Agent" },
];

const getRoleLabel = (roleVal) =>
  ROLES.find(r => r.value === roleVal)?.label || roleVal.replace(/_/g, ' ');

const BuyerAgreementTeam = ({ agreementId, parties: initialParties, userEmail, isExpanded, onToggle }) => {
  const { user } = useAuth0();
  const { isGuest, loaded: viewerLoaded } = useViewerSubscription();
  const canEditTeam = viewerLoaded && isGuest !== true;
  const [parties, setParties] = useState(initialParties || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [formData, setFormData] = useState({ role: 'buyer', name: '', email: '', phone: '', company: '' });

  useEffect(() => {
    if (initialParties) setParties(initialParties);
  }, [initialParties]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingParty(null);
    setFormData({ role: 'buyer', name: '', email: '', phone: '', company: '' });
    setShowModal(true);
  };

  const openEditModal = (party) => {
    setEditingParty(party);
    setFormData({
      role: party.role || 'buyer',
      name: party.name || '',
      email: party.email || '',
      phone: party.phone || '',
      company: party.company || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const callerEmail = userEmail || user?.email;
    try {
      setLoading(true);
      if (editingParty) {
        await axios.delete(
          `${BASE_URL}/buyer-agreement/${agreementId}/parties/${editingParty.id}`,
          { params: { user_email: callerEmail } }
        );
      }
      const res = await axios.post(
        `${BASE_URL}/buyer-agreement/${agreementId}/parties`,
        formData,
        { params: { user_email: callerEmail } }
      );
      setParties(prev => {
        const filtered = editingParty ? prev.filter(p => p.id !== editingParty.id) : prev;
        return [...filtered, res.data];
      });
      setShowModal(false);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingParty || !window.confirm('Remove this party?')) return;
    try {
      setLoading(true);
      await axios.delete(
        `${BASE_URL}/buyer-agreement/${agreementId}/parties/${editingParty.id}`,
        { params: { user_email: userEmail || user?.email } }
      );
      setParties(prev => prev.filter(p => p.id !== editingParty.id));
      setShowModal(false);
      setEditingParty(null);
    } catch (err) {
      alert('Failed to remove: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (!agreementId) return null;

  const buyers = parties.filter(p => p.role === 'buyer' || p.role === 'co_buyer');
  const agents = parties.filter(p => p.role !== 'buyer' && p.role !== 'co_buyer');

  const renderCard = (party, variant = 'standard') => {
    const isMinimal = variant === 'minimal';
    return (
      <div key={party.id} className={`team-member-card ${isMinimal ? 'card-minimal' : 'card-branded'}`}>
        {!isMinimal && (
          <div className="card-custom-header">
            <span className="header-role">{getRoleLabel(party.role)}</span>
            {canEditTeam && (
            <button className="edit-icon-btn header-edit" onClick={(e) => { e.stopPropagation(); openEditModal(party); }}>✎</button>
            )}
          </div>
        )}
        <div className="member-info">
          <div className="member-name-row">
            <div className="member-name-group">
              <span className="member-name">{party.name || 'Unknown Name'}</span>
              {party.company && <span className="member-company">{party.company}</span>}
            </div>
            {isMinimal && canEditTeam && (
              <button className="edit-icon-btn" onClick={(e) => { e.stopPropagation(); openEditModal(party); }}>✎</button>
            )}
          </div>
          <span className="member-email">{party.email || 'No Email'}</span>
          {party.phone && <span className="member-email">{formatPhoneForDisplay(party.phone)}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="transaction-team-container">
      <SectionHeader title="Key People" isExpanded={isExpanded} onToggle={onToggle}>
        <div className="section-controls">
          {canEditTeam && (
          <button
            className="add-member-header-btn"
            onClick={(e) => { e.stopPropagation(); openAddModal(); }}
            title="Add Party"
          >
            + Add Party
          </button>
          )}
        </div>
      </SectionHeader>

      {isExpanded && (
        <div className="team-content">
          {loading && <div className="loading">Loading...</div>}

          <div className="key-people-grid" style={{ gridTemplateColumns: '1fr 2fr' }}>
            <div className="people-column column-buyer">
              <div className="column-card">
                <h3 className="column-header column-header-navy">{buyers.length > 1 ? 'Buyers' : 'Buyer'}</h3>
                <div className="column-content">
                  {buyers.map(p => renderCard(p, 'minimal'))}
                </div>
              </div>
            </div>

            <div className="people-column column-others">
              <div className="column-card">
                <h3 className="column-header column-header-green">Buyer's Team</h3>
                <div className="column-content grid-others">
                  {agents.map(p => renderCard(p, 'standard'))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingParty ? 'Edit Party' : 'Add Party'}</h3>
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={!!editingParty && editingParty.role?.toLowerCase() !== 'participant'}
                  className={!!editingParty && editingParty.role?.toLowerCase() !== 'participant' ? 'disabled-input' : ''}
                >
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Name <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" />
              </div>
              <div className="form-group">
                <label>Company / Brokerage</label>
                <input type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Company name" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email address" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <PhoneNumberInput name="phone" value={formData.phone} onChange={handleInputChange} placeholder="e.g. 405-555-1234" />
              </div>
              <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                <div>
                  {editingParty && (
                    <button type="button" className="btn-danger" onClick={handleDelete}>Remove Party</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingParty ? 'Save Changes' : 'Add Party')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerAgreementTeam;
